/**
 * F1 -- the fuller definitions, in one place, linkable from anywhere.
 *
 * Every Explainer's "Learn more" lands on its own anchor here, so a
 * reader arrives at the term they were reading about rather than the
 * top of a list.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GLOSSARY, GLOSSARY_KEYS } from "shared/glossary/terms";
import { MarsadIcon } from "shared/components/MarsadIcon";

export function GlossaryView() {
  const { t } = useTranslation();
  return (
    <article data-testid="glossary-view" className="max-w-3xl space-y-6">
      <header>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("glossary.rubric")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="obligation"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("glossary.heading")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("glossary.body")}</p>
      </header>

      <dl className="space-y-5">
        {GLOSSARY_KEYS.map((key) => {
          const term = GLOSSARY[key]!;
          return (
            /* C4 -- each entry takes its direction from its own text.
               The glossary is untranslated English prose inside an RTL
               document, so every definition's closing full stop led the
               line instead of ending it. `dir="auto"` reads the first
               strong character, so this is correct before AND after a
               translation lands. */
            <div key={key} id={key} dir="auto" data-testid={`glossary-term-${key}`}>
              <dt className="text-sm font-semibold text-slate-900">{term.term}</dt>
              {term.aka && term.aka.length > 0 && (
                <p className="text-[11px] text-slate-400">
                  {t("glossary.alsoCalled", { names: term.aka.join(", ") })}
                </p>
              )}
              <dd className="mt-1 text-sm text-slate-600">{term.short}</dd>
              {term.more && (
                <dd className="mt-1 text-sm text-slate-500">{term.more}</dd>
              )}
              {/* ROUND 13 H6, thirteenth round of asking -- where you
                  DO this. Twenty-seven definitions and not one route:
                  a glossary that teaches the word and not the product
                  is a dictionary. A term with no single place gets no
                  link, because a destination invented to fill the
                  column sends a reader somewhere that does not answer
                  them. */}
              {term.where && (
                <dd className="mt-1">
                  <Link
                    to={term.where}
                    data-testid={`glossary-where-${key}`}
                    className="text-xs font-medium text-brand-brassDark hover:underline"
                  >
                    {t("glossary.whereYouDoThis")}
                  </Link>
                </dd>
              )}
            </div>
          );
        })}
      </dl>
    </article>
  );
}
